import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Crown } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * <FoundersWall /> — public, unauthenticated section listing the 3 CyBoard
 * founders with avatars, names, and gold FOUNDER badges. Displays at the
 * bottom of the public login page so visitors discover who built CyBoard
 * before they sign up. Founders who haven't registered yet still appear
 * with a "Coming soon" indicator instead of a profile link.
 */
export function FoundersWall() {
  const [founders, setFounders] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/founders`);
        if (!cancelled) setFounders(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setFounders([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || founders.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
      className="w-full max-w-sm mt-10"
      data-testid="founders-wall"
    >
      <div className="flex items-center justify-center gap-2 mb-4">
        <Crown size={14} weight="fill" className="text-[#FFD700]" />
        <h3 className="text-[#888] text-[11px] uppercase tracking-[0.2em]">Built by</h3>
        <Crown size={14} weight="fill" className="text-[#FFD700]" />
      </div>
      <div
        className={`grid gap-2 ${founders.length === 1 ? 'grid-cols-1' : founders.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}
      >
        {founders.map((f) => (
          <FounderCard key={f.name} founder={f} />
        ))}
      </div>
      <p className="text-[10px] text-[#444] text-center mt-3 italic">
        The humans behind CyBoard
      </p>
    </motion.section>
  );
}

function FounderCard({ founder }) {
  const initials = (founder.name || 'F').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  const isLink = founder.registered && founder.id;

  const card = (
    <div
      className={`
        relative bg-gradient-to-b from-[#1a1325] to-[#0e0e16]
        border border-[#FFB347]/30 rounded-xl p-3 text-center
        transition-all duration-200
        ${isLink ? 'hover:border-[#FFB347]/60 hover:scale-[1.03] cursor-pointer' : 'opacity-70'}
      `}
      data-testid={`founder-card-${founder.name?.split(' ')[0]?.toLowerCase()}`}
    >
      {/* Avatar circle */}
      <div className="relative inline-block mb-2">
        {founder.avatar ? (
          <img
            src={founder.avatar}
            alt={founder.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-[#FFB347]/40 mx-auto"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-white text-xs font-bold border-2 border-[#FFB347]/40 mx-auto">
            {initials}
          </div>
        )}
        {/* Crown bezel */}
        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FFD700]/20 border border-[#FFD700]/60 flex items-center justify-center">
          <Crown size={10} weight="fill" className="text-[#FFD700]" />
        </div>
      </div>
      {/* Name */}
      <p className="text-white text-[11px] font-bold leading-tight break-words line-clamp-2 min-h-[26px]" title={founder.name}>
        {founder.name}
      </p>
      {/* Status pill */}
      {founder.registered ? (
        <p className="text-[#FFD700] text-[8px] uppercase tracking-wider mt-1 font-bold">
          FOUNDER
        </p>
      ) : (
        <p className="text-[#666] text-[8px] uppercase tracking-wider mt-1">
          Joining soon
        </p>
      )}
    </div>
  );

  if (isLink) {
    return (
      <a href={`/profile/${founder.id}`} className="block">
        {card}
      </a>
    );
  }
  return card;
}
